// Arduino Nano en 3D (para protoboard).
import * as THREE from 'three';
import { Builder, M, mat, canvas, texture, text, extrudeBoard, boardShape, glow, setGlow, emissiveMat, setEmissive } from '../kit.js';
import { NANO } from '../../parts/boards.js';
import { getPins } from '../../parts/registry.js';
import { usbCable } from './usb.js';

const W = NANO.W;
const D = NANO.D;
const TOP = NANO.PCB_TOP;
let silk = null;

function drawSilk(pins) {
  const { c, g } = canvas(W, D, 24);
  const X = (x) => x + W / 2;
  const Y = (z) => z + D / 2;
  g.fillStyle = '#0b7d8e';
  g.fillRect(0, 0, W, D);
  g.strokeStyle = 'rgba(4,70,82,0.5)';
  g.lineWidth = 0.22;
  for (let i = 0; i < 18; i++) {
    g.beginPath();
    g.moveTo(X(-18 + i * 2.1), Y(-4));
    g.lineTo(X(-17 + i * 2.1), Y(-1));
    g.lineTo(X(-17 + i * 2.1), Y(3.5));
    g.stroke();
  }
  for (const p of pins) {
    g.fillStyle = '#d8b465';
    g.beginPath();
    g.arc(X(p.x), Y(p.z), 0.95, 0, Math.PI * 2);
    g.fill();
    g.fillStyle = '#121214';
    g.beginPath();
    g.arc(X(p.x), Y(p.z), 0.45, 0, Math.PI * 2);
    g.fill();
    const label = p.name.replace('.1', '').replace('.2', '').replace('RESET', 'RST').replace('GND', 'GND');
    const lbl = /^\d+$/.test(label) ? (label === '0' ? 'RX0' : label === '1' ? 'TX1' : 'D' + label) : label === '3.3V' ? '3V3' : label;
    text(g, lbl, X(p.x), Y(p.z) + (p.z < 0 ? 2.1 : -2.1), { size: 0.85, color: '#f1f4f4', weight: 700, rotate: -Math.PI / 2 });
  }
  text(g, 'ARDUINO', X(6), Y(-1.6), { size: 1.4, color: '#f1f4f4', weight: 800 });
  text(g, 'NANO', X(6), Y(0.6), { size: 1.9, color: '#f1f4f4', weight: 800 });
  text(g, 'L  PWR  TX  RX', X(-10), Y(1.9), { size: 0.75, color: '#f1f4f4', weight: 700 });
  const t = texture(c);
  t.userData.shared = true;
  return t;
}

export function buildNano(def, props) {
  const pins = getPins(def.type, props);
  if (!silk) silk = drawSilk(pins);
  const b = new Builder();
  const pcb = extrudeBoard(boardShape(W, D, { r: 0.6 }), NANO.PCB_T, silk, '#0a6d7c', { w: W, d: D, y0: NANO.PCB_Y });
  b.root.add(pcb);
  // Tiras de pines: separador negro y patas doradas
  for (const z of [-7.62, 7.62]) {
    b.box(15 * 2.54, 2.5, 2.54, M.blackPlastic, 0, 0.1, z);
  }
  for (const p of pins) {
    b.box(0.64, TOP + 0.9, 0.64, M.gold, p.x, 0, p.z);
    b.cyl(0.85, 0.85, 0.35, M.tin, p.x, TOP, p.z, 10);
  }
  // Mini USB, chip, regulador, cristal y botón
  b.box(9, 4, 7.6, M.metal, -W / 2 + 3.4, TOP, 0);
  b.box(0.2, 2.2, 5.6, M.hole, -W / 2 - 1.1, TOP + 0.9, 0);
  b.box(7, 1.2, 7, M.chip, 4.5, TOP, 0, { ry: Math.PI / 4 });
  b.box(3.4, 1.4, 3.2, M.chip, -12.5, TOP, -3.2);
  b.rbox(3.2, 1.0, 1.6, 0.4, M.metal, -6.5, TOP, 3.6);
  b.box(3.2, 1.4, 3.2, M.metal, 13.8, TOP, 0);
  b.build();
  const cap = new THREE.Mesh(new THREE.CylinderGeometry(0.9, 0.9, 0.8, 16), mat('#f0efe8', { roughness: 0.5 }));
  cap.position.set(13.8, TOP + 1.8, 0);
  cap.userData.interactive = { kind: 'press', key: 'reset', label: 'RESET' };
  b.root.add(cap);
  // Cable USB (prop «usb»): conector mini-B
  const usb = usbCable('mini', -W / 2 - 1.1, TOP + 2, 0, [9, 4, 7.6]);
  b.root.add(usb.group, usb.hit);
  usb.group.visible = props.usb !== false;
  const leds = {};
  [['L', -14.7, '#ffb02e'], ['ON', -12.5, '#36ff4a'], ['TX', -10.3, '#ffcc1a'], ['RX', -8.1, '#ffcc1a']].forEach(([name, x, g]) => {
    const m = emissiveMat('#f2efe6', g);
    const mesh = new THREE.Mesh(new THREE.BoxGeometry(1.6, 0.6, 0.8), m);
    mesh.position.set(x, TOP + 0.3, 3.2);
    b.root.add(mesh);
    const halo = glow(g, 7);
    halo.position.set(x, TOP + 1.3, 3.2);
    b.root.add(halo);
    leds[name] = { m, halo, g };
  });
  const view = {
    root: b.root,
    update(v) {
      const set = (n, on) => {
        setEmissive(leds[n].m, on, 14);
        leds[n].m.color.set(on > 0.05 ? leds[n].g : '#f2efe6');
        setGlow(leds[n].halo, on * 0.85);
      };
      set('L', v.L || 0);
      set('TX', v.TX ? 1 : 0);
      set('RX', v.RX ? 1 : 0);
      set('ON', 0.8 * (v.ON === true ? 1 : Number(v.ON) || 0));
    },
    reset() {
      view.update({});
    },
    press(key, down) {
      if (key === 'reset') cap.position.y = TOP + (down ? 1.45 : 1.8);
    },
    setProps(p) {
      usb.group.visible = p.usb !== false;
    },
  };
  view.reset();
  return view;
}
